import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { API_ENDPOINT, API_URL, Constants } from '../app-level-constants';
import { EncryptionService } from './encryption.service';

export type PersonaRole = 'Dealer' | 'Distributor' | 'L1 team' | 'TML-Technician' | 'TML-Plant';

export interface ApprovalStatusResult {
  latest_upload?: {
    approval_status: boolean | null;
    images_base64?: string[];
    presigned_urls?: string[];
  } | null;
  action_history?: {
    reason: string;
    action_taken_by: string;
    action_taken_at: string;
    approval_status: boolean;
  }[];
}

export interface PriceModel {
  id: number;
  model: string;
  fg_part_no: string;
  cyl_blk: number;
  crank: number;
  cyl_head: number;
  camshaft_1_pc: number;
  conrod_1_pc: number;
}

export interface ApiResponse<T = object | object[]> {
  result: T;
  status?: string;
  message?: string;
  crm_response?: T;
  serial_number?: string;
  errors?: Record<string, string>[];
  error?: Error;
  warnings?: Record<string, string>[];
  count?: number;
  current_page?: number;
  next?: any;
  previous?: any;
  total_pages?: number;
}

interface Error {
  result?: string;
  message?: string;
  status?: number;
}

export interface CheckMailRes {
  result: {
    send_mail: boolean;
  };
  status?: string;
  message?: string;
}

export interface AddressResponse {
  result: {
    address_line_1: string;
    address_line_2: string;
    dealer_code: string;
    division_id: string;
  };
  status?: string;
  message?: string;
}
export interface NotificationResponse {
  result: {
    notification_content: string;
    icon_number: number;
    is_read: boolean;
    created_at: string;
  }[];
  status?: string;
  message?: string;
}

export interface _551Cresponse {
  status?: string;
  message?: string;
  result: {
    is_ewb_required?: boolean;
    errors?: Record<string, string> | undefined;
    _551c_details?: {};
  };
  errors?: Record<string, string> | undefined;
}

export interface RefNoResponse {
  result: {
    reference_number: string;
  };
  status?: string;
  message?: string;
}

export interface MailsResponse {
  result: {
    dealer_emails: string[];
  };
  status?: string;
  message?: string;
}

export interface PriceResponse {
  result: {
    dealer_cdc_amount: number;
    technician_external_cdc: number;
    technician_internal_cdc: number;
  };
  status?: string;
  message?: string;
}

export interface MFAResponse {
  is_2fa_enabled: boolean;
}

export interface Decryptres {
  user_details: Data;
  enc: string;
  token: string;
}

export interface StoreData {
  user_role: string;
  crm_data: Data;
}

export interface Data {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  position: string;
  dealer_code: string;
}

export interface PdfResponse {
  presigned_url: string;
  core_invoice_date: Date;
  result: {
    core_invoice_date: Date;
    presigned_url: string;
    base64_pdf: string;
  };
}

export interface SetApprovalStatus<T = object | object[]> {
  result: { approval_status: string };
  status?: string;
  message?: string;
  crm_response?: T;
  serial_number?: string;
}

export interface Response {
  status?: string;
  distributor_email?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiCommunicationService {
  private readonly selectedPersona = new BehaviorSubject<StoreData | null>(null);
  personaDetails = this.selectedPersona.asObservable();
  domainNamePort = API_URL;

  constructor(
    private readonly http: HttpClient,
    private readonly encryptionService: EncryptionService
  ) {
    const storedPersona = localStorage.getItem('userPersona');
    if (storedPersona) {
      this.selectedPersona.next(JSON.parse(storedPersona));
    }
  }

  appLevelConstants = Constants;

  setPersona(data: StoreData) {
    localStorage.setItem('userPersona', JSON.stringify(data));
    this.selectedPersona.next(data);
  }

  getPersona(): object | null {
    return this.selectedPersona.getValue();
  }

  clearPersona() {
    localStorage.removeItem('userPersona');
    this.selectedPersona.next(null);
  }
  // private encryptedPostJSON<T>(url: string, reqObject: object): Observable<T> {
  //   return from(this.encryptionService.encrypt(reqObject)).pipe(
  //     switchMap(encryptedBody => {
  //       const body = { encrypted: encryptedBody };
  //       return this.http.post<T>(url, body, {
  //         headers: { 'Content-Type': 'application/json' },
  //       });
  //     })
  //   );
  // }

  getApplicationHeaders() {
    return {
      headers: new HttpHeaders({
        'Content-type': 'application/json',
        Authorization: this.getKeyCloakToken() ?? '',
      }),
    };
  }
  getApplicationHeadersFormData() {
    return {
      headers: new HttpHeaders({
        // 'Content-Type': 'multipart/form-data',
        Authorization: this.getKeyCloakToken() ?? '',
      }),
    };
  }

  getKeyCloakToken() {
    try {
      const token = localStorage.getItem('auth_token');
      return token ? 'Bearer ' + token : null;
    } catch (error) {
      localStorage.clear();
      window.location.href = '/login';
      window.location.reload();
      console.log('error decryptStorageData==>', error);
      return;
    }
  }

  formatToDDMMYYYYHHMM(date: Date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  getUserData() {
    let userData;
    this.personaDetails.subscribe({
      next: value => {
        userData = value;
        if (userData) {
          return userData.crm_data.first_name + ' ' + userData.crm_data.last_name;
        } else {
          return '-';
        }
      },
      error: error => {
        console.log(error);
        return '-';
      },
    });
  }

  getCoreList(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_DEALER_CORE_LIST, reqObject, this.getApplicationHeaders());
  }
  getCoreListV2(reqObject: object, page: any, page_size: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this.domainNamePort + API_ENDPOINT.GET_CORE_DETAILS_V2 + '?page=' + page + '&page_size=' + page_size,
      reqObject,
      this.getApplicationHeaders()
    );
  }
  getCoreListForDownload(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_CORE_DETAILS_V2, reqObject, this.getApplicationHeaders());
  }

  getDealerList(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_DEALER_LIST, reqObj, this.getApplicationHeaders());
  }
  getFilterParams(reqObj: object): Observable<any> {
    return this.http.post<any>(this.domainNamePort + API_ENDPOINT.GET_FILTER_PARAMS, reqObj, this.getApplicationHeaders());
  }
  getStateOffice(reqObj: object): Observable<any> {
    return this.http.post<any>(this.domainNamePort + API_ENDPOINT.GET_STATE_OFFICE, reqObj, this.getApplicationHeaders());
  }
  getL1CoreList(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_L1_CORE_LIST, reqObj, this.getApplicationHeaders());
  }

  getKpiDealer(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_KPI_DEALER, reqObject, this.getApplicationHeaders());
  }

  getPricelist(reqObj: object): Observable<PriceModel> {
    return this.http.post<PriceModel>(this.domainNamePort + API_ENDPOINT.GET_PRICELIST, reqObj, this.getApplicationHeaders());
  }

  getKpiDistributor(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_KPI_DISTRIBUTOR, reqObject, this.getApplicationHeaders());
  }

  getCoreDetails(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_CORE_DETAILS, reqObj, this.getApplicationHeaders());
  }

  getChecklist(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_CHECKLIST, reqObject, this.getApplicationHeaders());
  }

  saveAnwer(reqObj: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_ANSWER, reqObj, this.getApplicationHeadersFormData());
  }

  updateDeficiencyAmount(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.UPDATE_DEFICIENCY_AMOUNT, reqObj, this.getApplicationHeaders());
  }

  getDeficiencyAmount(reqObj: object): Observable<PriceResponse> {
    return this.http.post<PriceResponse>(this.domainNamePort + API_ENDPOINT.GET_DEFICIENCY_AMOUNT, reqObj, this.getApplicationHeaders());
  }

  rejectCore(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.REJECT_CORE, reqObj, this.getApplicationHeaders());
  }

  cdcComplete(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.CDC_COMPLETED, reqObj, this.getApplicationHeaders());
  }

  dealerImagesUpload(reqObj: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.UPLOAD_UPDATE_IMAGES, reqObj, this.getApplicationHeadersFormData());
  }

  setApprovalStatus(reqObj: object): Observable<ApiResponse> {
    return this.http.post<SetApprovalStatus>(this.domainNamePort + API_ENDPOINT.UPLOAD_UPDATE_IMAGES, reqObj, this.getApplicationHeaders());
  }

  getApprovalStatus(reqObject: object): Observable<ApiResponse<ApprovalStatusResult>> {
    return this.http.post<ApiResponse<ApprovalStatusResult>>(
      this.domainNamePort + API_ENDPOINT.GET_APPROVAL_STATUS,
      reqObject,
      this.getApplicationHeaders()
    );
  }

  getInspectionChecklist(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_CHECKLIST, reqObject, this.getApplicationHeaders());
  }

  setInspectionChecklist(reqObject: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_TML_CDC_ANSWER, reqObject, this.getApplicationHeaders());
  }

  updateChecklistStatus(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.UPDATE_CHECKLIST_STATUS, reqObj, this.getApplicationHeaders());
  }

  setCreateChat(reqObject: object): Observable<ApiResponse<ApiResponse>> {
    return this.http.post<ApiResponse<ApiResponse>>(this.domainNamePort + API_ENDPOINT.CREATE_CHAT, reqObject, this.getApplicationHeaders());
  }

  setSaveMessages(reqObject: FormData): Observable<ApiResponse<ApiResponse>> {
    return this.http.post<ApiResponse<ApiResponse>>(
      this.domainNamePort + API_ENDPOINT.SAVE_MESSAGES_CHAT,
      reqObject,
      this.getApplicationHeadersFormData()
    );
  }

  fetchMessages(reqObject: object): Observable<ApiResponse<ApiResponse>> {
    return this.http.post<ApiResponse<ApiResponse>>(this.domainNamePort + API_ENDPOINT.FETCH_CHAT, reqObject, this.getApplicationHeaders());
  }

  getKpiL1(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_L1_KPI, reqObj, this.getApplicationHeaders());
  }

  getKpiTechnician(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_TECH_KPI, reqObj, this.getApplicationHeaders());
  }

  closeConcludeChat(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.CLOSE_CONCLUDE_CHAT, reqObj, this.getApplicationHeaders());
  }

  ackCores(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.ACK_CORES, reqObj, this.getApplicationHeaders());
  }

  setCoreTracking(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.GET_CORE_TRACKING, reqObj, this.getApplicationHeaders());
  }

  fetchTracking(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_TRACKING, reqObj, this.getApplicationHeaders());
  }

  fetchLogin(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_LOGIN_DATA, reqObj, this.getApplicationHeaders());
  }

  validateInvoice(reqObj: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.OCR_INVOICE_VALIDATION, reqObj, this.getApplicationHeadersFormData());
  }

  getTmlKpi(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.TML_PLANT_KPI, reqObj, this.getApplicationHeaders());
  }

  getTmlListing(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.TML_PLANT_LISTING, reqObj, this.getApplicationHeaders());
  }

  validate551C(reqObj: FormData): Observable<_551Cresponse> {
    return this.http.post<_551Cresponse>(this.domainNamePort + API_ENDPOINT.VALIDATE_551C, reqObj, this.getApplicationHeadersFormData());
  }
  validate551CEway(reqObj: FormData): Observable<_551Cresponse> {
    return this.http.post<_551Cresponse>(this.domainNamePort + API_ENDPOINT.VALIDATE_551C_EWAY, reqObj, this.getApplicationHeadersFormData());
  }

  saveQR(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_QR, reqObj, this.getApplicationHeaders());
  }

  fetchQR(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_QR, reqObj, this.getApplicationHeaders());
  }

  saveOTC(reqObj: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_OTC, reqObj, this.getApplicationHeadersFormData());
  }

  save551CInvoice(reqObj: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_551C_FORM, reqObj, this.getApplicationHeadersFormData());
  }

  fetchOTC(reqObj: object): Observable<PdfResponse> {
    return this.http.post<PdfResponse>(this.domainNamePort + API_ENDPOINT.FETCH_OTC, reqObj, this.getApplicationHeaders());
  }

  saveEmail(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_EMAIL, reqObj, this.getApplicationHeaders());
  }

  saveDistributorEmail(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_DISTRIBUTOR_EMAIL, reqObj, this.getApplicationHeaders());
  }

  fetchDistributorEmail(reqObj: object): Observable<Response> {
    return this.http.post<Response>(this.domainNamePort + API_ENDPOINT.FETCH_DISTRIBUTOR_EMAIL, reqObj, this.getApplicationHeaders());
  }

  fetchEmail(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_FG_EMAIL, reqObj, this.getApplicationHeaders());
  }

  fetchPendingTechnician(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_PENDING_TECHNICIAN, reqObj, this.getApplicationHeaders());
  }

  reachedTechnician(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.REACHED_TECHNICIAN, reqObj, this.getApplicationHeaders());
  }

  fetchSerialNumber(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.FETCH_SERIAL_NUMBER, reqObj, this.getApplicationHeaders());
  }

  updateSerialNumber(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.UPDATE_SERIAL_NUMBER, reqObj, this.getApplicationHeaders());
  }

  uploadBillingExel(formData: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_BILLING_EXCEL, formData, this.getApplicationHeadersFormData());
  }

  uploadDebitExel(formData: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_DEBIT_EXCEL, formData, this.getApplicationHeadersFormData());
  }

  uploadGRAckExel(formData: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_GR_ACK_EXCEL, formData, this.getApplicationHeadersFormData());
  }

  uploadGRExel(formData: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_GR_EXCEL, formData, this.getApplicationHeadersFormData());
  }
  uploadMappingExel(formData: FormData): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.MAPPIND_EXCEL, formData, this.getApplicationHeadersFormData());
  }

  fetchCTRD(reqObj: object): Observable<PdfResponse> {
    return this.http.post<PdfResponse>(this.domainNamePort + API_ENDPOINT.FETCH_CTRD, reqObj, this.getApplicationHeaders());
  }

  sendEmail(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SEND_EMAIL, reqObj, this.getApplicationHeaders());
  }

  twofacheck(reqObj: object): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(this.domainNamePort + API_ENDPOINT.CHECK_2FA, reqObj);
  }

  sendEmailDistributor(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SEND_EMAIL_DISTRIBUTOR, reqObj, this.getApplicationHeaders());
  }

  saveEmails(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_DEALER_EMAILS, reqObj, this.getApplicationHeaders());
  }

  generatRefNumber(reqObj: object): Observable<RefNoResponse> {
    return this.http.post<RefNoResponse>(this.domainNamePort + API_ENDPOINT.GENERATE_REF_NUMBER, reqObj, this.getApplicationHeaders());
  }

  fetchDealerEmails(reqObj: object): Observable<MailsResponse> {
    return this.http.post<MailsResponse>(this.domainNamePort + API_ENDPOINT.FETCH_DEALER_EMAILS, reqObj, this.getApplicationHeaders());
  }

  checktwofa(reqObj: object): Observable<MFAResponse> {
    return this.http.post<MFAResponse>(this.domainNamePort + API_ENDPOINT.CHECK_2F_A, reqObj);
  }

  verifyTotp(reqObj: object): Observable<Decryptres> {
    return this.http.post<Decryptres>(this.domainNamePort + API_ENDPOINT.VERIFY_2F_A, reqObj);
  }

  private credentials: {
    password: string | null;
    user_type: string | null;
    salt: string | null;
  } = {
    password: null,
    user_type: null,
    salt: null,
  };

  private credentialsNew: {
    salt: string | null;
  } = {
    salt: null,
  };

  setCredentials(password: string, user_type: string, salt: string): void {
    this.credentials = { password, user_type, salt };
  }

  setCredsLogin(salt: string) {
    this.credentialsNew = { salt };
  }

  getCredsLogin(): { salt: string | null } {
    return this.credentialsNew;
  }

  getCredentials(): { password: string | null; user_type: string | null; salt: string | null } {
    return this.credentials;
  }

  clearCredentials(): void {
    this.credentials = { password: null, user_type: null, salt: null };
  }

  getDealerLocation(reqObj: object): Observable<AddressResponse> {
    return this.http.post<AddressResponse>(this.domainNamePort + API_ENDPOINT.GET_DEALER_LOCATION, reqObj, this.getApplicationHeaders());
  }

  saveNotification(reqObj: object): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.domainNamePort + API_ENDPOINT.SAVE_NOTIFICATION, reqObj, this.getApplicationHeaders());
  }

  fetchNotification(reqObj: object): Observable<NotificationResponse> {
    return this.http.post<NotificationResponse>(this.domainNamePort + API_ENDPOINT.FETCH_NOTIFICATION, reqObj, this.getApplicationHeaders());
  }

  checkMailStatus(reqObj: object): Observable<CheckMailRes> {
    return this.http.post<CheckMailRes>(this.domainNamePort + API_ENDPOINT.CHECK_MAIL_STATUS, reqObj, this.getApplicationHeaders());
  }
}

